// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as locationActions from '../actions';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { LocationService, LocationType } from '../../services/location.service';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { NotificationService } from '@fc-core/services/notification.service';
import { select, Store } from '@ngrx/store';
import { getFeatureList } from '../selectors';
import { LocationInterface } from '../../models/location-interface';
import { HttpErrorResponse } from '@angular/common/http';
import { getErrorMessage } from '@fc-core/services/notification.utils';

@Injectable()
export class MapEffects {
  private waitingForResponse: boolean;
  constructor(
    private actions$: Actions,
    private locationService: LocationService,
    private dialog: MatDialog,
    private router: Router,
    private notifications: NotificationService,
    private store: Store,
  ) {}

  saveLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(locationActions.saveFeature),
      switchMap(({ location }) => {
        return this.locationService.saveLocation(location).pipe(
          switchMap((locationData) => {
            if (location.photo instanceof File) {
              return this.locationService
                .addPhoto(location.photo, locationData.id)
                .pipe(
                  map((item) => {
                    this.router.navigate(['/locations']);
                    this.notifications.showSuccess(
                      'Location successfully added',
                    );
                    return locationActions.saveFeatureSuccess({
                      location: { ...locationData, photo: item.photo },
                    });
                  }),
                );
            } else {
              this.router.navigate(['/locations']);
              this.notifications.showSuccess('Location successfully added');
              return of(
                locationActions.saveFeatureSuccess({ location: locationData }),
              );
            }
          }),
          catchError((err) => {
            this.notifications.showError(getErrorMessage(err));
            return of(locationActions.mapError(err));
          }),
        );
      }),
    ),
  );

  addLocationType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(locationActions.addNewType),
      switchMap(({ locationType }) => {
        return this.locationService.addLocationType(locationType).pipe(
          map((type: LocationType) => {
            this.dialog.closeAll();
            this.notifications.showSuccess('Type successfully added');
            return locationActions.addNewTypeSuccess({ locationType: type });
          }),
          catchError((err) => {
            const errorMessage = getErrorMessage(err);
            this.notifications.showError(errorMessage);
            return of(locationActions.mapError(err));
          }),
        );
      }),
    ),
  );

  updateType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(locationActions.updateType),
      switchMap(({ locationType }) => {
        return this.locationService.updateLocationType(locationType).pipe(
          map((_type: LocationType) => {
            this.dialog.closeAll();
            this.notifications.showSuccess('Type successfully updated');
            return locationActions.updateTypeSuccess({ locationType });
          }),
          catchError((err) => {
            const errorMessage = getErrorMessage(err);
            this.notifications.showError(errorMessage);
            return of(locationActions.mapError(err));
          }),
        );
      }),
    ),
  );
  deleteType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(locationActions.deleteType),
      switchMap(({ locationType }) => {
        return this.locationService.deleteLocationType(locationType).pipe(
          map(() => {
            this.router.navigate(['/locations/main/location-types']);
            this.notifications.showSuccess(
              'Location type successfully deleted',
            );
            return locationActions.deleteTypeSuccess({ locationType });
          }),
          catchError((err) => {
            const errorMessage = getErrorMessage(err);
            this.notifications.showError(errorMessage);
            return of(locationActions.mapError(err));
          }),
        );
      }),
    ),
  );

  updateLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(locationActions.updateLocation),
      switchMap(({ location }) => {
        return this.locationService.updateLocation(location).pipe(
          switchMap((locationResponse) => {
            if (location.photo instanceof File) {
              return this.locationService
                .addPhoto(location.photo, locationResponse.id)
                .pipe(
                  map((item) => {
                    this.notifications.showSuccess(
                      'Location successfully updated',
                    );
                    this.router.navigate([
                      `/locations/main/location-list/${locationResponse.id}/`,
                    ]);
                    return locationActions.updateLocationSuccess({
                      location: item,
                    });
                  }),
                );
            } else {
              this.notifications.showSuccess('Location successfully updated');
              this.router.navigate([
                `/locations/main/location-list/${locationResponse.id}/`,
              ]);
              return of(
                locationActions.updateLocationSuccess({
                  location: locationResponse,
                }),
              );
            }
          }),
          catchError((err: HttpErrorResponse) => {
            const errorMessage = getErrorMessage(err);
            this.notifications.showError(errorMessage);
            return of(locationActions.mapError(err));
          }),
        );
      }),
    ),
  );

  deleteLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(locationActions.deleteLocation),
      switchMap(({ location }) => {
        return this.locationService.deleteLocation(location).pipe(
          map(() => {
            this.dialog.closeAll();
            this.router.navigate(['/locations']);
            this.notifications.showSuccess('Location successfully deleted');
            return locationActions.deleteLocationSuccess({ location });
          }),
          catchError((err) => {
            const errorMessage = getErrorMessage(err);
            this.notifications.showError(errorMessage);
            return of(locationActions.mapError(err));
          }),
        );
      }),
    ),
  );

  getLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(locationActions.getLocations),
      withLatestFrom(this.store.pipe(select(getFeatureList))),
      switchMap(([{ bbox, fields }, locationsFromStore]) => {
        return this.locationService.loadAllLocationsAsync(bbox, fields).pipe(
          switchMap(
            (locationsPayload: {
              locationList: LocationInterface[];
              count: number;
              end: boolean;
            }) => {
              const actions = [];
              if (
                locationsFromStore.length &&
                locationsFromStore.length === locationsPayload.count
              ) {
                actions.push(
                  locationActions.updateLocations({ update: locationsPayload }),
                );
              } else {
                actions.push(
                  locationActions.getLocationsSuccess({
                    data: locationsPayload,
                  }),
                );
              }
              return actions;
            },
          ),
          catchError((err) => of(locationActions.mapError(err))),
        );
      }),
    ),
  );

  getLocationTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(locationActions.loadLocationTypes),
      switchMap(() => {
        return this.locationService.getLocationTypes().pipe(
          map((types: LocationType[]) =>
            locationActions.loadLocationTypesSuccess({ locationTypes: types }),
          ),
          catchError((err) => of(locationActions.mapError(err))),
        );
      }),
    ),
  );
}
